import type { FC } from "preact/compat";
import { useEffect, useRef, useState } from "preact/hooks";
import { useClickOutside } from "@hooks/useClickOutside";
import { ComboboxSearchInput } from "../ComboboxSearchInput";
import { ComboboxListbox } from "../ComboboxListbox";
import { RoundedComboboxBtn } from "../ComboboxBtn";
import { SingleSelectListOption } from "../ComboboxListOption";
import type { Payors, SelectedItem, States } from "@services/filter-api/types";

type Data = {
  abbreviation?: string;
  filtered?: boolean;
  label: string;
  order?: number;
  payors?: Payors;
  searched?: boolean;
  selected?: boolean;
  slug: string;
  states?: States;
  value: string;
}

export type ComboboxProps = {
  optional?: boolean;
  placeholder?: string;
  isMobile?: boolean;
  required?: boolean;
  disabled_features?: Array<{ value: string }>
  selectedItem?: {
    value: string;
    label: string;
  } | null;
  data: Data[];
  open?: boolean;
  error?: string;
  handleOpenClick?: (open: boolean) => void;
  handleGetSelectedItems?: (
    items: {
      label: string;
      value: string;
      order?: number;
      selected: boolean;
    }[],
  ) => void;
  handleGetSelectedItem?: (item: SelectedItem) => void;
};
export const Combobox: FC<ComboboxProps> = ({
  data,
  handleGetSelectedItem,
  optional,
  handleOpenClick,
  open,
  placeholder,
  selectedItem,
  isMobile,
  error
}) => {
  const comboBoxRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [items, setItems] = useState<Data[]>(
    data.map((_item: any) => {
      return {
        ..._item,
        filtered: _item.filtered ?? false,
        searched: _item.searched ?? false,
        selected: false,
      };
    }),
  );

  useEffect(() => {
    if (JSON.stringify(items) !== JSON.stringify(data)) {
      setItems(
        items
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map((_item) => {
              const isFiltered = data.find(
                (_v) => _item.value === _v.value && _v.filtered,
              );
              if (isFiltered) {
                return {
                  ..._item,
                  filtered: true,
                  searched: false,
                  selected: _item.selected ?? false,
                };
              }
              return {
                ..._item,
                filtered: false,
                searched: _item.searched ?? false,
                selected: _item.selected ?? false,
              };
            }
          ),
      );
    }
  }, [data]);

  useEffect(() => {
    if (selectedItem) {
      setItems(
        items
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map((_item) => {
              return {
                ..._item,
                filtered: _item.filtered ?? false,
                searched: _item.searched ?? false,
                selected: _item.value === selectedItem.value,
              };
            }
          ),
      );
    }
  }, [selectedItem?.label, selectedItem?.value]);

  const handleClick = () => {
    if (isMobile) {
      document.body.style.overflow = 'hidden';
    }

    handleOpenClick?.(!open);
  };
  const handleClose = () => {
    document.body.style.overflow = '';
    handleOpenClick?.(false);
  };

  useClickOutside(comboBoxRef, () => {
    if (!isMobile) {
      handleClose();
    }
  });
  const handleSelectOption = (
    option: { value: string; label: string },
    checked: boolean,
  ) => {
    setSearchTerm("");
    const updatedItems = items.map((_item) => {
      return {
        ..._item,
        selected: _item.value === option.value ? checked : false
      }
    });
    setItems(updatedItems);
    const selected = updatedItems.find((_item) => _item.value === option.value);
    if (selected && selected.selected) {
      handleGetSelectedItem?.(selected);
    } else if (selected) {
      handleGetSelectedItem?.({
        value: selected.value,
        label: selected.label,
        selected: false,
        slug: selected.slug || '',
        abbreviation: selected.abbreviation
      });
    }
  };

  const handleSearchChange = (term: string) => {
    setSearchTerm(term);
    if (term) {
      const filteredItems = items.map((_item, index) => {
        // This was added for the location list search
        // This tries to match the state abbreviation first and prioritizes that
        if (_item?.abbreviation?.toLowerCase() === term.toLowerCase()) {
          return {
            ..._item,
            searched: true,
            weight: 1,
          };
        }

        if (_item.label.toLowerCase().includes(term.toLowerCase())) {
          return {
            ..._item,
            filtered: _item.filtered ?? false,
            searched: true,
            selected: _item.selected ?? false,
            weight: index,
          };
        }

        return {
          ..._item,
          filtered: _item.filtered ?? false,
          searched: false,
          selected: _item.selected ?? false,
          weight: 0,
        };
      })
        .sort((a, b) => a.weight < b.weight ? -1 : a.weight > b.weight ? 1 : 0)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

      setItems(filteredItems);
    } else {
      setItems(items.map((_item) => ({
        ..._item,
        filtered: _item.filtered ?? false,
        searched: false,
        selected: _item.selected ?? false,
      })));
    }
  };

  const renderedItems = () => {
    const fItems = items.filter((_item) => _item.filtered);
    if (searchTerm) {
      let sItems: Data[] = [];

      if (fItems.length) {
        sItems = items.filter((_item) => {
          return _item.searched && _item.filtered;
        });
      }
      if (!fItems.length) {
        sItems = items.filter((_item) => {
          return _item.searched;
        });
      }
      return sItems.map((_v) => (
        <SingleSelectListOption
          handleClose={handleClose}
          key={_v.value}
          value={_v.value}
          label={_v.label}
          selected={_v.selected}
          handleSelectOption={handleSelectOption}
        />
      ));
    }
    if (fItems.length) {
      return fItems.map((_v) => (
        <SingleSelectListOption
          handleClose={handleClose}
          key={_v.value}
          value={_v.value}
          label={_v.label}
          selected={_v.selected}
          handleSelectOption={handleSelectOption}
        />
      ));
    }

    if (!searchTerm && !fItems.length) {
      return items.map((_v) => (
        <SingleSelectListOption
          handleClose={handleClose}
          key={_v.value}
          value={_v.value}
          label={_v.label}
          selected={_v.selected}
          handleSelectOption={handleSelectOption}
        />
      ));
    }
  };

  return (
    <div className="relative" ref={comboBoxRef}>
      <RoundedComboboxBtn
        placeholder={placeholder}
        onClick={handleClick}
        error={error}
        open={open}
        labels={items
          .filter((_item) => _item.selected)
          .map((_item) => _item.label)}
      />
      {open && (
        <ComboboxListbox
          handleClose={handleClose}
          placeholder={placeholder}
          optional={optional}
          isMobile={isMobile}
          items={items.filter((_item) => _item.filtered)}
        >
          <ComboboxSearchInput
            term={searchTerm}
            handleSearchChange={handleSearchChange}
          />
          <>{renderedItems()}</>
        </ComboboxListbox>
      )}
    </div>
  );
};
